import React, { useState, useEffect, useRef } from "react";


import TextField from "../Commons/Forms/TextField";
import BtnAdd from "../Commons/Buttons/BtnAdd";
import BtnClear from "../Commons/Buttons/BtnClear";
import TextArea from "../Commons/Forms/TextArea";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { Checkbox, DatePicker, Modal, Radio, Select, Spin } from "antd";
import { formatLabel, getRole, getUser, formatDate } from "../Helpers/Utilities/Common";
import dayjs from "dayjs";
import {
  createPhysicalExam,
  createPhysicalExamWithFile,
  getDetailsForPE,
  getAbdomenLibrary,
  getSkinLibrary,
  getNeuroLibrary,
  getHeentLibrary,
  getChestLungsLibrary,
  getCardiovascularLibrary,
  getRectalLibrary,
  getGenitaliaLibrary,
  getPhysicalExam
} from "../Helpers/API/AssessmentApi/AssessmentApi";
import { toast } from "react-toastify";
import { endQueue } from "../Helpers/API/QueueApi/QueueApi";
import PhysicalExamDisplay from "../PhysicalExamHistory/PhysicalExamDisplay";
import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";
import backarrow from "../../Assets/Icons/back-arrow.png";
import { DATE_FORMAT } from "../../Utils/dateFormat";

const ESSENTIALLY_NORMAL_LABEL = /^essentially\s+normal$/i;
const OTHER_OPTION_LABEL = /^others?$/i;

const sortPeLibraryOptions = (options) =>
  [...options].sort((a, b) => {
    const aOther = OTHER_OPTION_LABEL.test(String(a.label || "").trim());
    const bOther = OTHER_OPTION_LABEL.test(String(b.label || "").trim());
    if (aOther && !bOther) return 1;
    if (!aOther && bOther) return -1;

    const aEssential = ESSENTIALLY_NORMAL_LABEL.test(String(a.label || "").trim());
    const bEssential = ESSENTIALLY_NORMAL_LABEL.test(String(b.label || "").trim());
    if (aEssential && !bEssential) return -1;
    if (!aEssential && bEssential) return 1;
    return String(a.label || "").localeCompare(String(b.label || ""), undefined, {
      sensitivity: "base",
    });
  });

const mapActiveLibraryOptions = (response) => {
  if (!response?.data?.data) return [];
  return sortPeLibraryOptions(
    response.data.data
      .filter((item) => item.status === "1")
      .map((item) => ({
        label: item.description || "",
        value: String(item.id || ""),
      }))
  );
};

function PhysicalExaminationYakap({
  addPEData = {},
  setAddPEData = null,
  patientDetails,
  doctorOptions,
  editData,
  setEditData,
  viewOnly = false,
}) {
  const role = getRole();
  const userId = getUser();
  const state = useLocation();
  const { id } = useParams();
  const patientId = id || state?.state?.patient_id || patientDetails?.id || "";
  const navigate = useNavigate();
  const [showLoader, setShowLoader] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const previewRef = useRef(null);

  const [localAddPEData, setLocalAddPEData] = useState(addPEData);
  const addPEDataSafe = addPEData || localAddPEData;
  const updateAddPEData = typeof setAddPEData === "function" ? setAddPEData : setLocalAddPEData;
  const [abdomenOptions, setAbdomenOptions] = useState([]);
  const [skinOptions, setSkinOptions] = useState([]);
  const [neurologicalOptions, setNeurologicalOptions] = useState([]);
  const [heentOptions, setHeentOptions] = useState([]);
  const [chestLungsOptions, setChestLungsOptions] = useState([]);
  const [cardiovascularOptions, setCardiovascularOptions] = useState([]);
  const [rectalOptions, setRectalOptions] = useState([]);
  const [genitaliaOptions, setGenitaliaOptions] = useState([]);
  // const currentDate = dayjs();

  const [medicalHistory, setMedicalHistory] = useState({
    allergies: { has: false, details: "" },
    hospitalizations: { has: false, details: "" },
    illnesses: { has: false, details: "" },
  });

  const [familyHistory, setFamilyHistory] = useState({
    hypertension: false,
    diabetes: false,
    asthma: false,
    cancer: false,
    cancerType: "",
    others: false,
    othersDetails: "",
    obGyneHistory: false,
    gravida: "",
    parity: "",
    lmp: "",
    pmp: "",
  });

  const [personalHistory, setPersonalHistory] = useState({
    alcohol: { has: false, details: "" },
    smoking: { has: false, details: "" },
    drugs: { has: false, details: "" },
  });

  const [vitalSigns, setVitalSigns] = useState({
    blood_pressure: "",
    pulse_rate: "",
    respiratory_rate: "",
    temperature: "",
    o2_sat: "",
    height: "",
    weight: "",
    bmi: "",
    lmp: "",
    left_eye_wo_correction: "",
    right_eye_wo_correction: "",
    left_eye_w_correction: "",
    right_eye_w_correction: "",
  });

  const [reviewSystems, setReviewSystems] = useState({
    general: { has: false, details: "" },
    vision: { has: false, details: "" },
    head_and_neck: { has: false, details: "" },
    ears: { has: false, details: "" },
    respiratory: { has: false, details: "" },
    cardiovascular: { has: false, details: "" },
    gastrointestinal: { has: false, details: "" },
    genitourinary: { has: false, details: "" },
    hematology: { has: false, details: "" },
    obgyn: { has: false, details: "" },
    neurological: { has: false, details: "" },
    musculoskeletal: { has: false, details: "" },
    skin: { has: false, details: "" },
  });

  const [physicalExam, setPhysicalExam] = useState({
    general: { status: "normal", notes: "" },
    skin: { status: "normal", notes: "", values: [] },
    breast: { status: "normal", notes: "" },
    nose_and_sinuses: { status: "normal", notes: "" },
    chest_and_lungs: { status: "normal", notes: "", values: [] },
    back: { status: "normal", notes: "" },
    cardiovascular: { status: "normal", notes: "", values: [] },
    extremities: { status: "normal", notes: "" },
    abdomen: { status: "normal", notes: "", values: [] },
    head_and_neck: { status: "normal", notes: "" },
    eyes_and_ears: { status: "normal", notes: "", values: [] }, 
    neurological: { status: "normal", notes: "", values: [] },
    rectal: { status: "normal", notes: "", values: [] },
    genitalia: { status: "normal", notes: "", values: [] },
  });

//   const abdomenOptions = [
//     { label: "Flat", value: "1" },
//     { label: "Hyperactive Bowel Sounds", value: "10" },
//     { label: "Palpable mass(es)", value: "11" },
//     { label: "Tympanitic/dull abdomen", value: "12" },
//     { label: "Uterine Contraction", value: "13" },
//     { label: "Flabby", value: "2" },
//     { label: "Globullar", value: "3" },
//     { label: "Muscle Guarding", value: "4" },
//     { label: "Tenderness", value: "5" },
//     { label: "Palpable Mass", value: "6" },
//     { label: "Essentially Normal", value: "7" },
//     { label: "Abdominal Rigidity", value: "8" },
//     { label: "Abdominal Tenderness", value: "9" },
//     { label: "Others", value: "99" },
//   ];

//   const skinOptions = [
//     { label: "Essentially Normal", value: "1" },
//     { label: "Weak Pulses", value: "10" },
//     { label: "Clubbing", value: "2" },
//     { label: "Cold Clammy", value: "3" },
//     { label: "Cyanosis/Mottled Skin", value: "4" },
//     { label: "Edema/Swelling", value: "5" },
//     { label: "Decreased Mobility", value: "6" },
//     { label: "Pale Nailbeds", value: "7" },
//     { label: "Poor Skin Turgor", value: "8" },
//     { label: "Rashes/Petechiae", value: "9" },
//     { label: "Others", value: "99" },
//   ];

//   const neurologicalOptions = [
//     { label: "Developmental Delay", value: "101" },
//     { label: "Abnormal Reflex(es)", value: "102" },
//     { label: "Coordination Deficit", value: "103" },
//     { label: "Reflex Changes", value: "104" },
//     { label: "Altered Consciousness", value: "105" },
//     { label: "Seizure Activity", value: "106" },
//     { label: "Others", value: "199" },
//   ];


async function fetchLibraryOptions() {
  try {
    const [abdomenRes, skinRes, neuroRes, heentRes, chestRes, cardioRes, rectalRes, genitaliaRes] = await Promise.all([
      getAbdomenLibrary(),
      getSkinLibrary(),
      getNeuroLibrary(),
      getHeentLibrary(),
      getChestLungsLibrary(),
      getCardiovascularLibrary(),
      getRectalLibrary(),
      getGenitaliaLibrary(),
    ])

    let parsedAbdomen = []
    let parsedSkin = []
    let parsedNeuro = []
    let parsedHeent = []
    let parsedChestLungs = []
    let parsedCardiovascular = []
    let parsedRectal = []
    let parsedGenitalia = []

    parsedAbdomen = mapActiveLibraryOptions(abdomenRes);
    setAbdomenOptions(parsedAbdomen);

    parsedSkin = mapActiveLibraryOptions(skinRes);
    setSkinOptions(parsedSkin);

    parsedNeuro = mapActiveLibraryOptions(neuroRes);
    setNeurologicalOptions(parsedNeuro);

    parsedHeent = mapActiveLibraryOptions(heentRes);
    setHeentOptions(parsedHeent);

    parsedChestLungs = mapActiveLibraryOptions(chestRes);
    setChestLungsOptions(parsedChestLungs);

    parsedCardiovascular = mapActiveLibraryOptions(cardioRes);
    setCardiovascularOptions(parsedCardiovascular);

    parsedRectal = mapActiveLibraryOptions(rectalRes);
    setRectalOptions(parsedRectal);

    parsedGenitalia = mapActiveLibraryOptions(genitaliaRes);
    setGenitaliaOptions(parsedGenitalia);

    return { parsedAbdomen, parsedSkin, parsedNeuro, parsedHeent, parsedChestLungs, parsedCardiovascular, parsedRectal, parsedGenitalia }

  } catch (err) {
    console.error("Failed to fetch library options:", err)
    toast.error("Failed to load physical exam options.")
    return { parsedAbdomen: [], parsedSkin: [], parsedNeuro: [], parsedHeent: [], parsedChestLungs: [], parsedCardiovascular: [], parsedRectal: [], parsedGenitalia: [] }
  }
}

async function fetchPhysicalExam(libs) {
  const response = await getPhysicalExam({ patient_id: patientId })
  if (response?.data?.data?.length > 0) {
    const data = response.data.data[0]
    const {
      parsedAbdomen = [],
      parsedSkin = [],
      parsedNeuro = [],
      parsedHeent = [],
      parsedChestLungs = [],
      parsedCardiovascular = [],
      parsedRectal = [],
      parsedGenitalia = [],
    } = libs || {}

    const parseMultiValueById = (value, options, systemKey, othersValue) => {
      const rawValue = typeof value === "string" ? value.trim() : ""
      if (!rawValue) return { status: "normal", notes: "", values: [] }
      const ids = rawValue.split(",").map((item) => item.trim()).filter(Boolean)
      const matchedValues = ids.filter((id) =>
        options.some((option) => option.value === id)
      )
      const notes = options
        .filter((option) => matchedValues.includes(option.value))
        .map((option) => option.label)
        .join(", ")
      const result = {
        status: matchedValues.length > 0 ? "abnormal" : "normal",
        notes,
        values: matchedValues,
      }
      if (systemKey && matchedValues.includes("99") && othersValue) {
        result[`pe_${systemKey}_others`] = othersValue
      }
      return result
    }

    const parseExam = (value) => {
      if (!value || value === "Normal" || value === "normal")
        return { status: "normal", notes: "" }
      if (value === "NOT DONE" || value === "not_done")
        return { status: "not_done", notes: "" }
      return { status: "abnormal", notes: value }
    }

    setPhysicalExam({
      general:          parseExam(data.pe_general),
      skin:             parseMultiValueById(data.pe_skin, parsedSkin, "skin", data.pe_skin_others),
      breast:           parseExam(data.pe_breast),
      nose_and_sinuses: parseExam(data.pe_nose_sinuses),
      chest_and_lungs:  parseMultiValueById(data.pe_chest_and_lungs, parsedChestLungs, "chest_and_lungs", data.pe_chest_and_lungs_others),
      back:             parseExam(data.pe_back),
      cardiovascular:   parseMultiValueById(data.pe_cardiovascular, parsedCardiovascular, "cardiovascular", data.pe_cardiovascular_others),
      extremities:      parseExam(data.pe_extremities),
      abdomen:          parseMultiValueById(data.pe_abdomen, parsedAbdomen, "abdomen", data.pe_abdomen_others),
      head_and_neck:    parseExam(data.pe_head_and_neck),
      eyes_and_ears:    parseMultiValueById(data.pe_heent || data.pe_eyes_ears, parsedHeent, "eyes_and_ears", data.pe_heent_others),
      neurological:     parseMultiValueById(data.pe_neurological, parsedNeuro, "neurological", data.pe_neurological_others),
      rectal:           parseMultiValueById(data.pe_rectal, parsedRectal, "rectal", data.pe_rectal_others),
      genitalia:        parseMultiValueById(data.pe_genitalia, parsedGenitalia, "genitalia", data.pe_genitalia_others),
    })

    setVitalSigns({
      blood_pressure:          data.blood_pressure || "",
      pulse_rate:              data.pulse_rate || "",
      respiratory_rate:        data.respiratory_rate || "",
      temperature:             data.temperature || "",
      o2_sat:                  data.o2_sat || "",
      height:                  data.height || "",
      weight:                  data.weight || "",
      bmi:                     data.bmi || "",
      lmp:                     data.lmp || "",
      left_eye_wo_correction:  data.left_eye_wo_correction || "",
      right_eye_wo_correction: data.right_eye_wo_correction || "",
      left_eye_w_correction:   data.left_eye_w_correction || "",
      right_eye_w_correction:  data.right_eye_w_correction || "",
    })
  }
}
  // Handle patient information changes
  const handlePatientInfoChange = (field, value) => {
    updateAddPEData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle medical history changes
  const handleMedicalHistoryChange = (field, has, details = "") => {
    setMedicalHistory((prev) => ({
      ...prev,
      [field]: { has, details },
    }));
  };

  // Handle family history changes
  const handleFamilyHistoryChange = (field, value) => {
    setFamilyHistory((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle personal history changes
  const handlePersonalHistoryChange = (field, has, details = "") => {
    setPersonalHistory((prev) => ({
      ...prev,
      [field]: { has, details },
    }));
  };

  // Handle vital signs changes
  const handleVitalSignsChange = (field, value) => {
    setVitalSigns((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle review of systems changes
  const handleReviewSystemsChange = (system, has, details = "") => {
    setReviewSystems((prev) => ({
      ...prev,
      [system]: { has, details },
    }));
  };

  // Handle physical exam changes
  const handlePhysicalExamChange = (system, field, value) => {
    setPhysicalExam((prev) => ({
      ...prev,
      [system]: {
        ...prev[system],
        [field]: value,
      },
    }));
  };

  const handlePhysicalExamOthersChange = (system, field, value) => {
    setPhysicalExam((prev) => ({
      ...prev,
      [system]: {
        ...prev[system],
        [field]: value,
      },
    }));
  };

  const getPhysicalExamStateKey = (system) => (system === "heent" ? "eyes_and_ears" : system);

  const getPhysicalExamOtherFieldValue = (system) => {
    const stateKey = getPhysicalExamStateKey(system);
    if (stateKey === "eyes_and_ears") {
      return physicalExam?.[stateKey]?.pe_eyes_and_ears_others || "";
    }
    return physicalExam?.[stateKey]?.[`pe_${stateKey}_others`] || "";
  };

  const isOtherSelectionRequired = (system) => {
    const stateKey = getPhysicalExamStateKey(system);
    const values = physicalExam?.[stateKey]?.values || [];
    return values.some((value) => ["99", "199", "999"].includes(String(value).trim()));
  };

  const isPhysicalExamNextDisabled = [
    "abdomen",
    "skin",
    "neurological",
    "heent",
    "eyes_and_ears",
    "chest_and_lungs",
    "cardiovascular",
    "rectal",
    "genitalia",
  ].some((system) => {
    if (!isOtherSelectionRequired(system)) return false;
    const otherValue = getPhysicalExamOtherFieldValue(system)?.trim();
    return !otherValue;
  });

  const handlePhysicalExamDropdownChange = (system, value) => {
    const options =
      system === "skin" ? skinOptions
      : system === "neurological" ? neurologicalOptions
      : system === "heent" || system === "eyes_and_ears" ? heentOptions
      : system === "chest_and_lungs" ? chestLungsOptions
      : system === "cardiovascular" ? cardiovascularOptions
      : system === "rectal" ? rectalOptions
      : system === "genitalia" ? genitaliaOptions
      : abdomenOptions;

    const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
    const selectedLabels = options
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label)
      .join(", ");

    setPhysicalExam((prev) => ({
      ...prev,
      [system]: {
        status: selectedValues.length > 0 ? "abnormal" : "normal",
        notes: selectedLabels,
        values: selectedValues,
      },
    }));
  };


  // Build preview data from form state - matches API payload exactly
  const buildPreviewData = () => {
    const getPhysicalExamValue = (system) => {
      const status = physicalExam[system]?.status;
      const notes = physicalExam[system]?.notes;
      if (status === "normal") return "Normal";
      if (status === "not_done") return "NOT DONE";
      return notes || "Normal";
    };

    const getReviewSystemsValue = (system) => {
      if (reviewSystems[system]?.has) {
        return reviewSystems[system]?.details || "";
      }
      return "";
    };

    // const selectedDoctor = doctorOptions?.find(d => d.value === addPEDataSafe.doctor_id);

    // Build the exact payload that will be submitted
    const payload = {
      patient_id: patientId,
    //   doctor_id: addPEDataSafe.doctor_id || "",
      charged_to: addPEDataSafe.company_name || "",
      type: addPEDataSafe.type || "",
      transaction_id: addPEDataSafe.transaction_id || "",
      allergies:
        medicalHistory?.allergies?.has === false
          ? ""
          : medicalHistory?.allergies?.details,
      hospitalization:
        medicalHistory?.hospitalizations?.has === false
          ? ""
          : medicalHistory?.hospitalizations?.details,
      illness:
        medicalHistory?.illnesses?.has === false
          ? ""
          : medicalHistory?.illnesses?.details,
      hypertension: familyHistory?.hypertension === false ? "0" : "1",
      asthma: familyHistory?.asthma === false ? "0" : "1",
      diabetes: familyHistory?.diabetes === false ? "0" : "1",
      cancer: familyHistory?.cancer === false ? "" : familyHistory?.cancerType,
      other_disease:
        familyHistory?.others === false ? "" : familyHistory?.othersDetails,
      gravida: familyHistory?.gravida || "",
      parity: familyHistory?.parity || "",
      ob_gyne_lmp: familyHistory?.lmp || "",
      ob_gyne_pmp: familyHistory?.pmp || "",
      alcohol:
        personalHistory?.alcohol?.has === false
          ? ""
          : personalHistory?.alcohol?.details,
      smoking:
        personalHistory?.smoking?.has === false
          ? ""
          : personalHistory?.smoking?.details,
      drugs:
        personalHistory?.drugs?.has === false
          ? ""
          : personalHistory?.drugs?.details,
      blood_pressure: vitalSigns?.blood_pressure || "",
      height: vitalSigns?.height || "",
      weight: vitalSigns?.weight || "",
      pulse_rate: vitalSigns?.pulse_rate || "",
      respiratory_rate: vitalSigns?.respiratory_rate || "",
      bmi: vitalSigns?.bmi || "",
      temperature: vitalSigns?.temperature || "",
      o2_sat: vitalSigns?.o2_sat || "",
      left_eye_wo_correction: vitalSigns?.left_eye_wo_correction || "",
      right_eye_wo_correction: vitalSigns?.right_eye_wo_correction || "",
      left_eye_w_correction: vitalSigns?.left_eye_w_correction || "",
      right_eye_w_correction: vitalSigns?.right_eye_w_correction || "",
      lmp: vitalSigns?.lmp || "",
      rs_general:
        reviewSystems?.general?.has === false
          ? ""
          : reviewSystems?.general?.details,
      rs_head_and_neck:
        reviewSystems?.head_and_neck?.has === false
          ? ""
          : reviewSystems?.head_and_neck?.details,
      rs_respiratory:
        reviewSystems?.respiratory?.has === false
          ? ""
          : reviewSystems?.respiratory?.details,
      rs_gastrointestinal:
        reviewSystems?.gastrointestinal?.has === false
          ? ""
          : reviewSystems?.gastrointestinal?.details,
      rs_hematology:
        reviewSystems?.hematology?.has === false
          ? ""
          : reviewSystems?.hematology?.details,
      rs_neurological:
        reviewSystems?.neurological?.has === false
          ? ""
          : reviewSystems?.neurological?.details,
      rs_skin:
        reviewSystems?.skin?.has === false ? "" : reviewSystems?.skin?.details,
      rs_vision:
        reviewSystems?.vision?.has === false
          ? ""
          : reviewSystems?.vision?.details,
      rs_ears:
        reviewSystems?.ears?.has === false ? "" : reviewSystems?.ears?.details,
      rs_cardiovascular:
        reviewSystems?.cardiovascular?.has === false
          ? ""
          : reviewSystems?.cardiovascular?.details,
      rs_genitourinary:
        reviewSystems?.genitourinary?.has === false
          ? ""
          : reviewSystems?.genitourinary?.details,
    //   rs_obgyn:
    //     reviewSystems?.obgyn?.has === false
    //       ? ""
    //       : reviewSystems?.obgyn?.details,
    //   rs_musculoskeletal:
    //     reviewSystems?.musculoskeletal?.has === false
    //       ? ""
    //       : reviewSystems?.musculoskeletal?.details,
      pe_general:
        physicalExam?.general?.status === "normal"
          ? "Normal"
          : physicalExam?.general?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.general?.notes,
      pe_head_and_neck:
        physicalExam?.head_and_neck?.status === "normal"
          ? "Normal"
          : physicalExam?.head_and_neck?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.head_and_neck?.notes,
      pe_mouth_and_throat:
        physicalExam?.mouth_and_throat?.status === "normal"
          ? "Normal"
          : physicalExam?.mouth_and_throat?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.mouth_and_throat?.notes,
      pe_breast:
        physicalExam?.breast?.status === "normal"
          ? "Normal"
          : physicalExam?.breast?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.breast?.notes,
      pe_back:
        physicalExam?.back?.status === "normal"
          ? "Normal"
          : physicalExam?.back?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.back?.notes,
      pe_extremities:
        physicalExam?.extremities?.status === "normal"
          ? "Normal"
          : physicalExam?.extremities?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.extremities?.notes,
      pe_rectal: (physicalExam?.rectal?.values || []).join(",") || "",
      pe_skin: (physicalExam?.skin?.values || []).join(",") || "",
      // pe_eyes_ears:
      //   physicalExam?.eyes_and_ears?.status === "normal"
      //     ? "Normal"
      //     : physicalExam?.eyes_and_ears?.status === "not_done"
      //     ? "NOT DONE"
      //     : physicalExam?.eyes_and_ears?.notes,
      // pe_nose_sinuses:
      //   physicalExam?.nose_and_sinuses?.status === "normal"
      //     ? "Normal"
      //     : physicalExam?.nose_and_sinuses?.status === "not_done"
      //     ? "NOT DONE"
      //     : physicalExam?.nose_and_sinuses?.notes,
      pe_heent: (physicalExam?.eyes_and_ears?.values || []).join(",") || "",
      pe_chest_and_lungs: (physicalExam?.chest_and_lungs?.values || []).join(",") || "",
      pe_cardiovascular: (physicalExam?.cardiovascular?.values || []).join(",") || "",
      pe_abdomen: (physicalExam?.abdomen?.values || []).join(",") || "",
      pe_neurological: (physicalExam?.neurological?.values || []).join(",") || "",
      pe_genitalia: (physicalExam?.genitalia?.values || []).join(",") || "",
    };

    // Add display-only fields for the preview component
    return {
      ...payload,
      // Display fields
      formatted_type: addPEData?.type?.toUpperCase() || "",
      formatted_patient_name: patientDetails?.formatted_patient_name || `${patientDetails?.first_name || ""} ${patientDetails?.last_name || ""}`,
      patient_name: patientDetails?.formatted_patient_name || `${patientDetails?.first_name || ""} ${patientDetails?.last_name || ""}`,
      birthdate: patientDetails?.birthdate || "",
    //   doctor_name: selectedDoctor?.label || "-",
      sex: state?.state?.sex || state?.state?.patient_sex || patientDetails?.sex || "",
      patient_sex: state?.state?.patient_sex || state?.state?.sex || patientDetails?.sex || "",
      // Map for display (combine eyes_ears and nose_sinuses for display)
      pe_eyes_ears_nose: [payload.pe_eyes_ears, payload.pe_nose_sinuses].filter(v => v && v !== "Normal").join("; ") || "Normal",
    };
  };

  // Generate PDF from preview component
  const generatePDF = async () => {
    const input = previewRef.current;
    if (!input) return null;

    // Add class for html2pdf to see print-only elements
    input.classList.add("html2pdf__printing");

    // Get footer element and render to canvas
    const getFooterImage = async () => {
      const footerEl = input?.querySelector?.(".print-footer");
      if (!footerEl) return null;

      footerEl.classList.remove("print-only");
      footerEl.style.display = "block";

      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-9999px;top:0;width:800px;background:#fff;";
      container.appendChild(footerEl.cloneNode(true));
      document.body.appendChild(container);

      try {
        const canvas = await html2canvas(container, {
          scale: 1,
          useCORS: true,
          backgroundColor: "#ffffff",
        });
        return {
          dataUrl: canvas.toDataURL("image/jpeg", 0.6),
          width: canvas.width,
          height: canvas.height,
        };
      } finally {
        document.body.removeChild(container);
        footerEl.classList.add("print-only");
        footerEl.style.display = "";
      }
    };

    let footerImg = null;
    try {
      footerImg = await getFooterImage();
    } catch (err) {
      console.error("Footer render error:", err);
    }

    const options = {
      margin: [7, 8.5, 10, 8.5],
      image: { type: "jpeg", quality: 0.6 },
      html2canvas: { scale: 1, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: "css", before: "#pageBreak" },
    };

    return new Promise((resolve, reject) => {
      html2pdf()
        .set(options)
        .from(input)
        .toPdf()
        .get("pdf")
        .then((pdf) => {
          const totalPages = pdf.internal.getNumberOfPages();
          const pageW = pdf.internal.pageSize.getWidth();
          const pageH = pdf.internal.pageSize.getHeight();

          // Add footer to last page
          if (footerImg && totalPages > 0) {
            pdf.setPage(totalPages);
            const footerH = (footerImg.height / footerImg.width) * pageW;
            const footerY = pageH - footerH;
            pdf.addImage(footerImg.dataUrl, "JPEG", 0, footerY, pageW, footerH);
          }

          // Add page numbers
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(9);
            pdf.setFont("times", "italic");
            const pageNoY = (footerImg && i === totalPages)
              ? pageH - ((footerImg.height / footerImg.width) * pageW) - 5
              : pageH - 10;
            pdf.text(`Page ${i} of Physical Exam`, pageW - 50, pageNoY);
          }

          // Return as blob
          const blob = pdf.output("blob");
          input.classList.remove("html2pdf__printing");
          resolve(blob);
        })
        .catch((err) => {
          input.classList.remove("html2pdf__printing");
          reject(err);
        });
    });
  };

  // Function for creating Physical Exam API
  async function addPhysicalExam() {
    if (isPhysicalExamNextDisabled) {
      toast.error("Please provide details for the selected 'Others' option before continuing.");
      return;
    }

    setShowLoader(true);
    
    // Generate PDF from preview
    let pdfBlob = null;
    try {
      pdfBlob = await generatePDF();
    } catch (err) {
      console.error("PDF generation error:", err);
    }

    var payload = {
      patient_id: patientId,
    //   doctor_id: addPEDataSafe.doctor_id || "",
      charged_to: addPEDataSafe.company_name || "",
      type: addPEDataSafe.type || "",
      transaction_id: addPEDataSafe.transaction_id || "",
      allergies:
        medicalHistory?.allergies?.has === false
          ? ""
          : medicalHistory?.allergies?.details,
      hospitalization:
        medicalHistory?.hospitalizations?.has === false
          ? ""
          : medicalHistory?.hospitalizations?.details,
      illness:
        medicalHistory?.illnesses?.has === false
          ? ""
          : medicalHistory?.illnesses?.details,
      hypertension: familyHistory?.hypertension === false ? "0" : "1",
      asthma: familyHistory?.asthma === false ? "0" : "1",
      diabetes: familyHistory?.diabetes === false ? "0" : "1",
      cancer: familyHistory?.cancer === false ? "" : familyHistory?.cancerType,
      other_disease:
        familyHistory?.others === false ? "" : familyHistory?.othersDetails,
      gravida: familyHistory?.gravida || "",
      parity: familyHistory?.parity || "",
      ob_gyne_lmp: familyHistory?.lmp || "",
      ob_gyne_pmp: familyHistory?.pmp || "",
      alcohol:
        personalHistory?.alcohol?.has === false
          ? ""
          : personalHistory?.alcohol?.details,
      smoking:
        personalHistory?.smoking?.has === false
          ? ""
          : personalHistory?.smoking?.details,
      drugs:
        personalHistory?.drugs?.has === false
          ? ""
          : personalHistory?.drugs?.details,
      blood_pressure: vitalSigns?.blood_pressure || "",
      height: vitalSigns?.height || "",
      weight: vitalSigns?.weight || "",
      pulse_rate: vitalSigns?.pulse_rate || "",
      respiratory_rate: vitalSigns?.respiratory_rate || "",
      bmi: vitalSigns?.bmi || "",
      temperature: vitalSigns?.temperature || "",
      o2_sat: vitalSigns?.o2_sat || "",
      left_eye_wo_correction: vitalSigns?.left_eye_wo_correction || "",
      right_eye_wo_correction: vitalSigns?.right_eye_wo_correction || "",
      left_eye_w_correction: vitalSigns?.left_eye_w_correction || "",
      right_eye_w_correction: vitalSigns?.right_eye_w_correction || "",
      lmp: vitalSigns?.lmp || "",
      rs_general:
        reviewSystems?.general?.has === false
          ? ""
          : reviewSystems?.general?.details,
      rs_head_and_neck:
        reviewSystems?.head_and_neck?.has === false
          ? ""
          : reviewSystems?.head_and_neck?.details,
      rs_respiratory:
        reviewSystems?.respiratory?.has === false
          ? ""
          : reviewSystems?.respiratory?.details,
      rs_gastrointestinal:
        reviewSystems?.gastrointestinal?.has === false
          ? ""
          : reviewSystems?.gastrointestinal?.details,
      rs_hematology:
        reviewSystems?.hematology?.has === false
          ? ""
          : reviewSystems?.hematology?.details,
      rs_neurological:
        reviewSystems?.neurological?.has === false
          ? ""
          : reviewSystems?.neurological?.details,
      rs_skin:
        reviewSystems?.skin?.has === false ? "" : reviewSystems?.skin?.details,
      rs_vision:
        reviewSystems?.vision?.has === false
          ? ""
          : reviewSystems?.vision?.details,
      rs_ears:
        reviewSystems?.ears?.has === false ? "" : reviewSystems?.ears?.details,
      rs_cardiovascular:
        reviewSystems?.cardiovascular?.has === false
          ? ""
          : reviewSystems?.cardiovascular?.details,
      rs_genitourinary:
        reviewSystems?.genitourinary?.has === false
          ? ""
          : reviewSystems?.genitourinary?.details,
    //   rs_obgyn:
    //     reviewSystems?.obgyn?.has === false
    //       ? ""
    //       : reviewSystems?.obgyn?.details,
    //   rs_musculoskeletal:
    //     reviewSystems?.musculoskeletal?.has === false
    //       ? ""
    //       : reviewSystems?.musculoskeletal?.details,
      pe_general:
        physicalExam?.general?.status === "normal"
          ? "Normal"
          : physicalExam?.general?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.general?.notes,
      pe_head_and_neck:
        physicalExam?.head_and_neck?.status === "normal"
          ? "Normal"
          : physicalExam?.head_and_neck?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.head_and_neck?.notes,
      pe_mouth_and_throat:
        physicalExam?.mouth_and_throat?.status === "normal"
          ? "Normal"
          : physicalExam?.mouth_and_throat?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.mouth_and_throat?.notes,
      pe_breast:
        physicalExam?.breast?.status === "normal"
          ? "Normal"
          : physicalExam?.breast?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.breast?.notes,
      pe_back:
        physicalExam?.back?.status === "normal"
          ? "Normal"
          : physicalExam?.back?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.back?.notes,
      pe_extremities:
        physicalExam?.extremities?.status === "normal"
          ? "Normal"
          : physicalExam?.extremities?.status === "not_done"
          ? "NOT DONE"
          : physicalExam?.extremities?.notes,
      pe_rectal: (physicalExam?.rectal?.values || []).join(",") || "",
      pe_skin: (physicalExam?.skin?.values || []).join(",") || "",
      // pe_eyes_ears:
      //   physicalExam?.eyes_and_ears?.status === "normal"
      //     ? "Normal"
      //     : physicalExam?.eyes_and_ears?.status === "not_done"
      //     ? "NOT DONE"
      //     : physicalExam?.eyes_and_ears?.notes,
      // pe_nose_sinuses:
      //   physicalExam?.nose_and_sinuses?.status === "normal"
      //     ? "Normal"
      //     : physicalExam?.nose_and_sinuses?.status === "not_done"
      //     ? "NOT DONE"
      //     : physicalExam?.nose_and_sinuses?.notes,
      pe_heent: (physicalExam?.eyes_and_ears?.values || []).join(",") || "",
      pe_chest_and_lungs: (physicalExam?.chest_and_lungs?.values || []).join(",") || "",
      pe_cardiovascular: (physicalExam?.cardiovascular?.values || []).join(",") || "",
      pe_abdomen: (physicalExam?.abdomen?.values || []).join(",") || "",
      pe_neurological: (physicalExam?.neurological?.values || []).join(",") || "",
      pe_genitalia: (physicalExam?.genitalia?.values || []).join(",") || "",

      // Append "others" fields for appropriate exam systems
      pe_rectal_others: physicalExam?.rectal?.pe_rectal_others || "",
      pe_skin_others: physicalExam?.skin?.pe_skin_others || "",
      pe_heent_others: physicalExam?.eyes_and_ears?.pe_eyes_and_ears_others || "",
      pe_chest_and_lungs_others: physicalExam?.chest_and_lungs?.pe_chest_and_lungs_others || "",
      pe_cardiovascular_others: physicalExam?.cardiovascular?.pe_cardiovascular_others || "",
      pe_abdomen_others: physicalExam?.abdomen?.pe_abdomen_others || "",
      pe_neurological_others: physicalExam?.neurological?.pe_neurological_others || "",
      pe_genitalia_others: physicalExam?.genitalia?.pe_genitalia_others || "",
    };

    // Check if this is an edit or create
    if (editData?.id) {
      // Import and call update API
      const { updatePhysicalExam } = require("../Helpers/API/AssessmentApi/AssessmentApi");
      payload.id = editData.id;
      // Get transaction_id from editData or localStorage
      const peTransactionMap = JSON.parse(localStorage.getItem("pe_transaction_map") || "{}");
      payload.transaction_id = editData?.transaction_id || peTransactionMap[editData.id] || localStorage.getItem("pe_transaction_id");
      console.log("PhysicalExamination edit - transaction_id:", payload.transaction_id, "editData:", editData);
      const response = await updatePhysicalExam(payload, pdfBlob);
      if (response.error) {
        response.error?.data?.messages?.map((msg) => toast.error(msg));
        setShowLoader(false);
        return;
      }

      toast.success(response.data?.message || "Physical exam updated successfully");
      setShowPreview(false);
      setShowLoader(false);
      if (setEditData) setEditData(null);
      navigate(`/patients/profile/${patientId}`, {
        state: {
          openTab: "pe_history",
          selectedTab: "social_personal_history",
          fromConsult: state?.state?.fromConsult || false,
        },
      });
      return;
    }

    // Create new physical exam
    const response = await createPhysicalExamWithFile(payload, pdfBlob);
    if (response.error) {
      response.error?.data?.messages?.map((msg) => toast.error(msg));
      setShowLoader(false);
      return;
    }

    toast.success(response.data?.message || "Physical exam created successfully");
    setShowPreview(false);
    setShowLoader(false);
    setTimeout(() => {
    navigate(`/patients/profile/${patientId}`, {
        state: {
        fromConsult: state?.state?.fromConsult || false,
        queue_id: state?.state?.queueId || state?.state?.queue_id || "",
        queue_no: state?.state?.queueNo || state?.state?.queue_no || "",
        fromRegisterPatient: state?.state?.fromRegisterPatient || false,
        fromPhilhealthCheck: true,
        openTab: "pe_history",
        selectedTab: "social_personal_history",
        },
    })
    }, 1000);
  }

  async function handleEndPhysicalExamQueue() {
    const response = await endQueue({
      patient_id: patientId,
      transaction_id: addPEDataSafe.transaction_id || "",
    });
    if (response.error) {
      response.error?.data?.messages.map((data) => {
        return toast.error(data);
      });
      setShowLoader(false);
    } else {
      toast.success(response.data.message, { autoClose: 1000 });
      setShowLoader(false);
      setTimeout(() => {
        localStorage.removeItem("isCheck");
        localStorage.removeItem("isPE");
        navigate("/queue");
      }, 1000);
    }
  }

  async function fetchDetailsForPE() {
    const response = await getDetailsForPE({
      patient_id: patientId,
      transaction_id: state?.state?.transaction_id,
    });

    if (response?.data?.data) {
      const data = response.data.data;
      setVitalSigns({
        blood_pressure: data.vital_sign?.bp || "",
        pulse_rate: data.vital_sign?.heart_rate || "",
        respiratory_rate: data.vital_sign?.respiratory_rate || "",
        temperature: data.vital_sign?.temperature || "",
        o2_sat: data.vital_sign?.oxygen_saturation || "",
        height: data.vital_sign?.height || "",
        weight: data.vital_sign?.weight || "",
        bmi: data.vital_sign?.bmi || "",
        lmp: "",
        left_eye_wo_correction: data.vital_sign?.left_eye_wo_correction || "",
        right_eye_wo_correction: data.vital_sign?.right_eye_wo_correction || "",
        left_eye_w_correction: data.vital_sign?.left_eye_w_correction || "",
        right_eye_w_correction: data.vital_sign?.right_eye_w_correction || "",
      });
      setMedicalHistory({
        allergies: { has: !!data.allergy, details: data.allergy || "" },
        hospitalizations: {
          has: !!data.hospitalization,
          details: data.hospitalization || "",
        },
        illnesses: { has: !!data.illness, details: data.illness || "" },
      });
      setFamilyHistory({
        hypertension: data.hypertension === 1,
        asthma: data.asthma === 1,
        diabetes: data.diabetes === 1,
        cancer: !!data.cancer,
        cancerType: data.cancer || "",
        others: false,
        othersDetails: "",
      });
      updateAddPEData((prev) => ({
        ...prev,
        company_name: data.company_name || "",
      }));
    }
  }

  useEffect(() => {
    async function init() {
      setIsInitializing(true);
      try {
        if (!viewOnly && !editData?.id) {
          await fetchDetailsForPE();
        }
        const libs = await fetchLibraryOptions();
        await fetchPhysicalExam(libs);
      } finally {
        setIsInitializing(false);
      }
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewOnly, patientId]);

  // Populate form with edit data when editing
  useEffect(() => {
    if (editData && Object.keys(editData).length > 0) {
      populateFormFromEditData(editData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  const populateFormFromEditData = (examData) => {
    // Medical History
    setMedicalHistory({
      allergies: { has: !!examData.allergy, details: examData.allergy || "" },
      hospitalizations: { has: !!examData.hospitalization, details: examData.hospitalization || "" },
      illnesses: { has: !!examData.illness, details: examData.illness || "" },
    });

    // Family History
    setFamilyHistory({
      hypertension: examData.hypertension === "1" || examData.hypertension === 1,
      diabetes: examData.diabetes === "1" || examData.diabetes === 1,
      asthma: examData.asthma === "1" || examData.asthma === 1,
      cancer: !!examData.cancer,
      cancerType: examData.cancer || "",
      others: !!examData.other_disease,
      othersDetails: examData.other_disease || "",
      obGyneHistory: !!(examData.gravida || examData.parity || examData.ob_gyne_lmp || examData.ob_gyne_pmp),
      gravida: examData.gravida || "",
      parity: examData.parity || "",
      lmp: examData.ob_gyne_lmp || "",
      pmp: examData.ob_gyne_pmp || "",
    });

    // Personal History
    setPersonalHistory({
      alcohol: { has: !!examData.alcohol, details: examData.alcohol || "" },
      smoking: { has: !!examData.smoking, details: examData.smoking || "" },
      drugs: { has: !!examData.drugs, details: examData.drugs || "" },
    });

    // Vital Signs
    setVitalSigns({
      blood_pressure: examData.blood_pressure || "",
      pulse_rate: examData.pulse_rate || "",
      respiratory_rate: examData.respiratory_rate || "",
      temperature: examData.temperature || "",
      o2_sat: examData.o2_sat || "",
      height: examData.height || "",
      weight: examData.weight || "",
      bmi: examData.bmi || "",
      lmp: examData.lmp || "",
      left_eye_wo_correction: examData.left_eye_wo_correction || "",
      right_eye_wo_correction: examData.right_eye_wo_correction || "",
      left_eye_w_correction: examData.left_eye_w_correction || "",
      right_eye_w_correction: examData.right_eye_w_correction || "",
    });

    // Review of Systems
    const parseReviewSystem = (value) => {
      if (!value || value === "Normal" || value === "normal") {
        return { has: false, details: "" };
      }
      return { has: true, details: value };
    };

    setReviewSystems({
      general: parseReviewSystem(examData.rs_general),
      vision: parseReviewSystem(examData.rs_vision),
      head_and_neck: parseReviewSystem(examData.rs_head_and_neck),
      ears: parseReviewSystem(examData.rs_ears),
      respiratory: parseReviewSystem(examData.rs_respiratory),
      cardiovascular: parseReviewSystem(examData.rs_cardiovascular),
      gastrointestinal: parseReviewSystem(examData.rs_gastrointestinal),
      genitourinary: parseReviewSystem(examData.rs_genitourinary),
      hematology: parseReviewSystem(examData.rs_hematology),
    //   obgyn: parseReviewSystem(examData.rs_obgyn),
      neurological: parseReviewSystem(examData.rs_neurological),
    //   musculoskeletal: parseReviewSystem(examData.rs_musculoskeletal),
      skin: parseReviewSystem(examData.rs_skin),
    });

    // Physical Exam
    const parseMultiValue = (value, options) => {
      const rawValue = typeof value === "string" ? value.trim() : "";
      if (!rawValue) {
        return { status: "normal", notes: "", values: [] };
      }

      const values = rawValue
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

      const matchedValues = values.filter((item) =>
        options.some((option) => option.value === item)
      );

      const notes = options
        .filter((option) => matchedValues.includes(option.value))
        .map((option) => option.label)
        .join(", ");

      return {
        status: matchedValues.length > 0 ? "abnormal" : "normal",
        notes,
        values: matchedValues,
      };
    };

    const parseAbdomenValue = (value) => parseMultiValue(value, abdomenOptions);

    const parsePhysicalExam = (value, system) => {
      if (value === "Normal" || value === "normal") {
        return { status: "normal", notes: "", values: [] };
      }
      if (value === "NOT DONE" || value === "not_done") {
        return { status: "not_done", notes: "", values: [] };
      }
      if (system === "abdomen") {
        return parseAbdomenValue(value);
      }
      if (system === "skin") {
        return parseMultiValue(value, skinOptions);
      }
      if (system === "neurological") {
        return parseMultiValue(value, neurologicalOptions);
      }
      return { status: "abnormal", notes: value || "", values: [] };
    };

    setPhysicalExam({
      general: parsePhysicalExam(examData.pe_general),
      skin: parsePhysicalExam(examData.pe_skin),
      breast: parsePhysicalExam(examData.pe_breast),
      nose_and_sinuses: parsePhysicalExam(examData.pe_nose_sinuses),
      // mouth_and_throat: parsePhysicalExam(examData.pe_mouth_and_throat),
      chest_and_lungs: parsePhysicalExam(examData.pe_chest_and_lungs),
      back: parsePhysicalExam(examData.pe_back),
      cardiovascular: parsePhysicalExam(examData.pe_cardiovascular),
      extremities: parsePhysicalExam(examData.pe_extremities),
      abdomen: parsePhysicalExam(examData.pe_abdomen, "abdomen"),
      head_and_neck: parsePhysicalExam(examData.pe_head_and_neck),
      eyes_and_ears: parsePhysicalExam(examData.pe_eyes_ears),
      neurological: parsePhysicalExam(examData.pe_neurological),
      rectal: parsePhysicalExam(examData.pe_rectal),
      genitalia: parsePhysicalExam(examData.pe_genitalia),
    });

    // Patient Info - use formatted_type since type is a React component
    const typeValue = examData.formatted_type?.toLowerCase() || examData.type || "ape";
    updateAddPEData((prev) => ({
      ...prev,
      company_name: examData.charged_to || "",
      type: typeof typeValue === 'string' ? typeValue : "ape",
    //   doctor_id: examData.doctor_id || null,
      editId: examData.id || null, // Track edit ID
    }));
  };

  // Check if logged-in user is a doctor (specialist, pcp, or director)
  const isDoctor = role === "specialist" || role === "pcp" || role === "director";

  useEffect(() => {
    // Skip this effect if we're in edit mode - edit data will be populated separately
    if (editData?.id) return;
    
    updateAddPEData((prev) => {
      // If logged-in user is a doctor, use their ID as the examining physician
    //   const doctorId = isDoctor ? userId : (state?.state?.doctor_id || prev.doctor_id);
      
      return {
        ...prev,
        // doctor_id: doctorId,
        company_name: state?.state?.company_name,
        transaction_id: state?.state?.transaction_id,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, isDoctor, userId]);

  console.log(physicalExam);

  return (
    <div className={`tabs-box p-3${viewOnly ? " text-start" : ""}`}>
      {(showLoader || isInitializing) && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.25)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
        </div>
      )}
      <div className="col pt-1">
        <div className="">
          {!viewOnly && (
            <div className="row pt-2 p-3">
              <div className="col-1 text-center mt-2">
                <img
                  src={backarrow}
                  alt="back"
                  className="backarrow"
                  width={25}
                  height={25}
                  onClick={() =>
                    navigate(
                      state?.state?.months <= 24
                        ? "/patients/vitals/" + patientId
                        : "/patients/ncdq/" + patientId
                    )
                  }
                ></img>
              </div>
              <div className="col font-big font-bold">Physical Examination</div>
            </div>
          )}
          <div className="row mt-2">
            <div className="col-sm-12">
              <hr />
              {/* <div className="row">
                <div className="col ms-3">
                  <label className="col font-bold font-medium ">
                    PATIENT INFORMATION
                  </label>
                  <div className="row my-1">
                    <div className="col mb-2 ps-3">
                      <label>Company Name/HMO</label>
                      <TextField
                        value={addPEDataSafe.company_name || ""}
                        onChange={(e) =>
                          handlePatientInfoChange("company", e.target.value)
                        }
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-3 mb-2 mt-4">
                      <Radio.Group
                        value={addPEData?.type}
                        onChange={(e) =>
                          handlePatientInfoChange("type", e.target.value)
                        }
                      >
                        <Radio value="ape">APE</Radio>
                        <Radio value="peme">PEME</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                </div>
              </div> */}
              {/* <hr /> */}
              {/* <div className="row"> */}
                {/* <div className="col ms-3">
                  <label className="col font-bold font-medium ">
                    PAST MEDICAL HISTORY
                  </label>
                  {["allergies", "hospitalizations", "illnesses"].map(
                    (item) => (
                      <div key={item} className="row mb-2">
                        <div className="col ms-2">
                          <label className="font-bold my-1 text-capitalize">
                            {item}
                          </label>
                          <div className="d-flex align-items-center mt-1">
                            <Radio.Group
                              value={medicalHistory[item].has ? "yes" : "no"}
                              onChange={(e) =>
                                handleMedicalHistoryChange(
                                  item,
                                  e.target.value === "yes"
                                )
                              }
                              className="ml-2"
                            >
                              <Radio value="no">NO</Radio>
                              <Radio value="yes" className="ml-3">
                                YES, Please elaborate.
                              </Radio>
                            </Radio.Group>
                            <Checkbox
                              checked={!medicalHistory[item].has}
                              onChange={() =>
                                handleMedicalHistoryChange(item, false)
                              }
                            >
                              NO
                            </Checkbox>
                            <Checkbox
                              checked={medicalHistory[item].has}
                              onChange={() =>
                                handleMedicalHistoryChange(item, true)
                              }
                              className="ml-3"
                            >
                              YES, Please elaborate.
                            </Checkbox>
                          </div>
                          {medicalHistory[item].has && (
                            <div className="mt-3 ms-1">
                              <TextArea
                                smallPlaceholder
                                value={medicalHistory[item].details}
                                onChange={(e) =>
                                  handleMedicalHistoryChange(
                                    item,
                                    true,
                                    e.target.value
                                  )
                                }
                                rows={1}
                                placeholder={`Please provide details about ${item}.`}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div> */}
                {/* <div className="col ms-3">
                  <label className="col font-bold font-medium ">
                    FAMILY HISTORY/HEREDOFAMILIAL DISEASES <br />
                    (Please check if applicable.)
                  </label>
                  <div className="row mt-2">
                    <div className="col-12 my-1">
                      <Checkbox
                        checked={familyHistory.hypertension}
                        onChange={(e) =>
                          handleFamilyHistoryChange(
                            "hypertension",
                            e.target.checked
                          )
                        }
                      >
                        HYPERTENSION
                      </Checkbox>
                    </div>
                    <div className="col-12 my-1">
                      <Checkbox
                        checked={familyHistory.asthma}
                        onChange={(e) =>
                          handleFamilyHistoryChange("asthma", e.target.checked)
                        }
                      >
                        ASTHMA
                      </Checkbox>
                    </div>
                    <div className="col-12 my-1">
                      <Checkbox
                        checked={familyHistory.diabetes}
                        onChange={(e) =>
                          handleFamilyHistoryChange(
                            "diabetes",
                            e.target.checked
                          )
                        }
                      >
                        DIABETES
                      </Checkbox>
                    </div>
                    <div className="col-12 my-1">
                      <Checkbox
                        checked={familyHistory.cancer}
                        onChange={(e) =>
                          handleFamilyHistoryChange("cancer", e.target.checked)
                        }
                      >
                        CANCER, please specify.
                      </Checkbox>
                      {familyHistory.cancer && (
                        <TextArea
                          smallPlaceholder
                          value={familyHistory.cancerType}
                          onChange={(e) =>
                            handleFamilyHistoryChange(
                              "cancerType",
                              e.target.value
                            )
                          }
                          rows={1}
                          placeholder={`Please provide details.`}
                        />
                      )}
                    </div>
                    <div className="col-12 my-1">
                      <Checkbox
                        checked={familyHistory.others}
                        onChange={(e) =>
                          handleFamilyHistoryChange("others", e.target.checked)
                        }
                      >
                        OTHERS, please specify.
                      </Checkbox>
                      {familyHistory.others && (
                        <TextArea
                          smallPlaceholder
                          value={familyHistory.othersDetails}
                          onChange={(e) =>
                            handleFamilyHistoryChange(
                              "othersDetails",
                              e.target.value
                            )
                          }
                          rows={1}
                          placeholder={`Please provide details.`}
                        />
                      )}
                    </div>
                    {(state?.state?.sex?.toUpperCase() === "FEMALE" ||
                      state?.state?.patient_sex?.toUpperCase() === "FEMALE" ||
                      patientDetails?.sex?.toUpperCase() === "FEMALE") && (
                      <div className="col-12 my-1">
                        <Checkbox
                          checked={familyHistory.obGyneHistory}
                          onChange={(e) =>
                            handleFamilyHistoryChange("obGyneHistory", e.target.checked)
                          }
                        >
                          OB-GYNE HISTORY
                        </Checkbox>
                        {familyHistory.obGyneHistory && (
                          <div className="row mt-2 ms-1">
                            <div className="col-md-6 mb-2">
                              <label>Gravida</label>
                              <TextField
                                value={familyHistory.gravida}
                                onChange={(e) =>
                                  handleFamilyHistoryChange("gravida", e.target.value)
                                }
                                placeholder=""
                              />
                            </div>
                            <div className="col-md-6 mb-2">
                              <label>Parity</label>
                              <TextField
                                value={familyHistory.parity}
                                onChange={(e) =>
                                  handleFamilyHistoryChange("parity", e.target.value)
                                }
                                placeholder=""
                              />
                            </div>
                            <div className="col-md-6 mb-2">
                              <label>LMP</label>
                              <TextField
                                value={familyHistory.lmp}
                                onChange={(e) =>
                                  handleFamilyHistoryChange("lmp", e.target.value)
                                }
                                placeholder=""
                              />
                            </div>
                            <div className="col-md-6 mb-2">
                              <label>PMP</label>
                              <TextField
                                value={familyHistory.pmp}
                                onChange={(e) =>
                                  handleFamilyHistoryChange("pmp", e.target.value)
                                }
                                placeholder=""
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col ms-3">
                  <label className="col font-bold font-medium ">
                    PERSONAL - SOCIAL HISTORY
                  </label>
                  {["alcohol", "smoking", "drugs"].map((item) => (
                    <div key={item} className="row mb-2">
                      <div className="col ms-2">
                        <label className="font-bold my-1 text-capitalize">
                          {item}
                        </label>
                        <div className="d-flex align-items-center mt-1">
                          <Checkbox
                            checked={!personalHistory[item].has}
                            onChange={() =>
                              handlePersonalHistoryChange(item, false)
                            }
                          >
                            NO
                          </Checkbox>
                          <Checkbox
                            checked={personalHistory[item].has}
                            onChange={() =>
                              handlePersonalHistoryChange(item, true)
                            }
                            className="ml-3"
                          >
                            YES, Please elaborate.
                          </Checkbox>
                        </div>
                        {personalHistory[item].has && (
                          <div className="mt-3 ms-1">
                            <TextArea
                              smallPlaceholder
                              value={personalHistory[item].details}
                              onChange={(e) =>
                                handlePersonalHistoryChange(
                                  item,
                                  true,
                                  e.target.value
                                )
                              }
                              rows={1}
                              placeholder={`Please provide details.`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div> */}
              {/* <hr /> */}
              <div className="row">
                <div className="col ms-3">
                  <label className="d-block w-100 font-bold font-medium">
                    NURSING ASSESSMENT - VITAL SIGNS
                  </label>
                  <div className="row">
                    <div className="col ms-2">
                      <div className="row mb-2 my-1">
                        <div className="col">
                          <label>Blood Pressure</label>
                          <TextField
                            value={vitalSigns.blood_pressure}
                            onChange={(e) =>
                              handleVitalSignsChange(
                                "blood_pressure",
                                e.target.value
                              )
                            }
                            suffix="mmHg"
                            disabled={true}
                          />
                        </div>
                        <div className="col">
                          <label>Height</label>
                           <TextField
                            value={vitalSigns.height}
                            onChange={(e) =>
                              handleVitalSignsChange("height", e.target.value)
                            }
                            suffix="cm"
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col">
                          <label>Pulse Rate</label>
                          <TextField
                            value={vitalSigns.pulse_rate}
                            onChange={(e) =>
                              handleVitalSignsChange(
                                "pulse_rate",
                                e.target.value
                              )
                            }
                            suffix="bpm"
                            disabled={true}
                          />
                        </div>
                        <div className="col">
                          <label>Weight</label>
                          <TextField
                            value={vitalSigns.weight}
                            onChange={(e) =>
                              handleVitalSignsChange("weight", e.target.value)
                            }
                            suffix="kg"
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col">
                          <label>Respiratory Rate</label>
                          <TextField
                            value={vitalSigns.respiratory_rate}
                            onChange={(e) =>
                              handleVitalSignsChange(
                                "respiratory_rate",
                                e.target.value
                              )
                            }
                            suffix="cpm"
                            disabled={true}
                          />
                        </div>
                        <div className="col">
                          <label>BMI</label>
                          <TextField value={vitalSigns.bmi} disabled={true} />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col">
                          <label>Temperature</label>
                          <TextField
                            value={vitalSigns.temperature}
                            onChange={(e) =>
                              handleVitalSignsChange(
                                "temperature",
                                e.target.value
                              )
                            }
                            suffix="°C"
                            disabled={true}
                          />
                        </div>
                        <div className="col">
                          <label>O2 Sat</label>
                          <TextField
                            value={vitalSigns.o2_sat}
                            onChange={(e) =>
                              handleVitalSignsChange("o2_sat", e.target.value)
                            }
                            disabled={true}
                          />
                        </div>
                      </div>
                      {(state?.state?.sex?.toUpperCase() === "FEMALE" ||
                        state?.state?.patient_sex?.toUpperCase() === "FEMALE" ||
                        patientDetails?.sex?.toUpperCase() === "FEMALE") && (
                        <>
                          <div className="row mb-2">
                            <div className="col-6">
                              <label>LMP (for females only)</label>
                              <DatePicker format={DATE_FORMAT}
                                value={
                                  vitalSigns.lmp ? dayjs(vitalSigns.lmp) : null
                                }
                                onChange={(date) =>
                                  handleVitalSignsChange(
                                    "lmp",
                                    date ? date.format("YYYY-MM-DD") : ""
                                  )
                                }
                                className="w-100"
                                disabled={viewOnly}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col ms-3">
                  <label className="d-block w-100 font-bold font-medium">
                    VISUAL ACUITY
                  </label>
                  <div className="row">
                    <div className="row mb-2 my-1">
                      <div className="col ms-2">
                        <label>Without correction - Left eye</label>
                        <TextField
                          value={vitalSigns.left_eye_wo_correction}
                          onChange={(e) =>
                            handleVitalSignsChange(
                              "left_eye_wo_correction",
                              e.target.value
                            )
                          }
                          disabled={viewOnly}
                        />
                      </div>
                      <div className="col ms-2">
                        <label>Without correction - Right eye</label>
                        <TextField
                          value={vitalSigns.right_eye_wo_correction}
                          onChange={(e) =>
                            handleVitalSignsChange(
                              "right_eye_wo_correction",
                              e.target.value
                            )
                          }
                          disabled={viewOnly}
                        />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col ms-2">
                        <label>With glasses/contact lenses - Left eye</label>
                        <TextField
                          value={vitalSigns.left_eye_w_correction}
                          onChange={(e) =>
                            handleVitalSignsChange(
                              "left_eye_w_correction",
                              e.target.value
                            )
                          }
                          disabled={viewOnly}
                        />
                      </div>
                      <div className="col ms-2">
                        <label>With glasses/contact lenses - Right eye</label>
                        <TextField
                          value={vitalSigns.right_eye_w_correction}
                          onChange={(e) =>
                            handleVitalSignsChange(
                              "right_eye_w_correction",
                              e.target.value
                            )
                          }
                          disabled={viewOnly}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <hr />
              {/* <div className="row">
                <div className="col ms-3">
                  <label className="col font-bold font-medium ">
                    REVIEW OF SYSTEMS
                  </label>
                  <div className="row">
                    {Object.keys(reviewSystems).map((system) => (
                      <div key={system} className="col-6 mb-2 ps-3">
                        <label className="font-bold my-1 text-capitalize">
                          {formatLabel(system)}
                        </label>
                        <div className="d-flex align-items-center mb-1">
                          <Checkbox
                            checked={!reviewSystems[system].has}
                            onChange={() =>
                              handleReviewSystemsChange(system, false)
                            }
                          >
                            NO
                          </Checkbox>
                          <Checkbox
                            checked={reviewSystems[system].has}
                            onChange={() =>
                              handleReviewSystemsChange(system, true)
                            }
                            className="ml-3"
                          >
                            YES, please specify.
                          </Checkbox>
                        </div>
                        {reviewSystems[system].has && (
                          <TextArea
                            smallPlaceholder
                            value={reviewSystems[system].details}
                            onChange={(e) =>
                              handleReviewSystemsChange(
                                system,
                                true,
                                e.target.value
                              )
                            }
                            rows={1}
                            className="mt-1"
                            placeholder={`Please provide details.`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div> */}
              {/* <hr /> */}
              <div className="row">
                <div className="col ms-3">
                  <label className="d-block w-100 font-bold font-medium">
                    PHYSICAL EXAMINATION
                  </label>
                  <div className="row">
                    {Object.keys(physicalExam).map((system) => {
                      // Comment out head_and_neck and nose_and_sinuses
                      if (system === "head_and_neck" || system === "nose_and_sinuses") return null;

                      return (
                        <div key={system} className="col-md-6 mb-2 ps-3">
                          <label className="font-bold my-1 text-capitalize">
                            {["heent", "eyes_and_ears"].includes(system)
                              ? "Head, Eyes, Ears, Nose, Throat (HEENT)"
                              : formatLabel(system)}
                          </label>
                          {(system !== "abdomen" && system !== "skin" && system !== "neurological" && system !== "heent" && system !== "eyes_and_ears"&& system !== "chest_and_lungs" && system !== "cardiovascular" && system !== "rectal" && system !== "genitalia") && (
                            <div className="d-flex align-items-center mb-1">
                              <Checkbox
                                checked={physicalExam[system].status === "normal"}
                                onChange={() => handlePhysicalExamChange(system, "status", "normal")}
                                disabled={viewOnly}
                              >
                                NORMAL
                              </Checkbox>
                              <Checkbox
                                checked={physicalExam[system].status === "abnormal"}
                                onChange={() => handlePhysicalExamChange(system, "status", "abnormal")}
                                className="ml-2"
                                disabled={viewOnly}
                              >
                                ABNORMAL
                              </Checkbox>
                              <Checkbox
                                checked={physicalExam[system].status === "not_done"}
                                onChange={() => handlePhysicalExamChange(system, "status", "not_done")}
                                className="ml-2"
                                disabled={viewOnly}
                              >
                                NOT DONE
                              </Checkbox>
                            </div>
                          )}
                          {(system === "abdomen" || system === "skin" || system === "neurological" || system === "heent" || system === "eyes_and_ears" || system === "chest_and_lungs" || system === "cardiovascular" || system === "rectal" || system === "genitalia") ? (
                            <>
                              <Select
                                mode="multiple"
                                className="w-100 mt-1"
                                value={physicalExam[system].values}
                                placeholder={
                                  system === "abdomen" ? "Select abdomen finding(s)"
                                  : system === "skin" ? "Select skin finding(s)"
                                  : system === "neurological" ? "Select neurological finding(s)"
                                  : system === "chest_and_lungs" ? "Select chest & lungs finding(s)"
                                  : system === "cardiovascular" ? "Select cardiovascular finding(s)"
                                  : system === "rectal" ? "Select rectal finding(s)"
                                  : system === "genitalia" ? "Select genitalia finding(s)"
                                  : "Select HEENT finding(s)"
                                }
                                options={
                                  system === "abdomen" ? abdomenOptions
                                  : system === "skin" ? skinOptions
                                  : system === "neurological" ? neurologicalOptions
                                  : system === "chest_and_lungs" ? chestLungsOptions
                                  : system === "cardiovascular" ? cardiovascularOptions
                                  : system === "rectal" ? rectalOptions
                                  : system === "genitalia" ? genitaliaOptions
                                  : heentOptions
                                }
                                onChange={(value) => handlePhysicalExamDropdownChange(system, value)}
                                allowClear
                                disabled={viewOnly}
                              />
                              {isOtherSelectionRequired(system) && (
                                <div className="mt-1">
                                  <div className="d-flex align-items-center gap-1 mb-1 text-danger small">
                                    <span>Required</span>
                                    <span aria-hidden="true">*</span>
                                  </div>
                                  <TextArea
                                    smallPlaceholder
                                    value={getPhysicalExamOtherFieldValue(system)}
                                    onChange={(e) => {
                                      const stateKey = getPhysicalExamStateKey(system);
                                      const field = stateKey === "eyes_and_ears" ? "pe_eyes_and_ears_others" : `pe_${stateKey}_others`;
                                      handlePhysicalExamOthersChange(stateKey, field, e.target.value);
                                    }}
                                    rows={1}
                                    className="mt-1"
                                    placeholder="Please specify others."
                                    disabled={viewOnly}
                                  />
                                </div>
                              )}
                            </>
                          ) : (
                            physicalExam[system].status === "abnormal" && (
                              <TextArea
                                smallPlaceholder
                                value={physicalExam[system].notes}
                                onChange={(e) => handlePhysicalExamChange(system, "notes", e.target.value)}
                                rows={1}
                                className="mt-1"
                                placeholder="Please specify abnormal findings."
                                disabled={viewOnly}
                              />
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <hr />
              {/* <div className="row">
                <div className="col-6 mb-2 ps-3">
                  <label>Examining Physician</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Doctor"
                    value={addPEData?.doctor_id}
                    // onChange={(value) => handleDoctorChange(value)}
                    allowClear
                    options={doctorOptions}
                    optionFilterProp="label"
                    showSearch
                    disabled
                  />
                </div>
                <div className="col-6 mb-2 ps-3">
                  <label>Date Performed</label>
                  <DatePicker
                    value={addPEData?.date_performed}
                    className="w-100"
                    format={DATE_FORMAT}
                    disabled
                  />
                </div>
              </div> */}
            </div>
          </div>
          {!viewOnly && (
            <Row className="justify-content-end mt-5 mb-3">
              <Col xs={2} className="col-container">
                <BtnAdd
                  label={"Next"}
                  size={"sm"}
                  onClick={() => addPhysicalExam()}
                  isClicked={showLoader}
                  disabled={isPhysicalExamNextDisabled}
                />
              </Col>
            </Row>
          )}
        </div>
      </div>

      {!viewOnly && (
        <Modal
          open={showPreview}
          onCancel={() => setShowPreview(false)}
          footer={null}
          width={800}
          className="center"
        >
          <div ref={previewRef}>
            <PhysicalExamDisplay data={buildPreviewData()} showFooter={true} />
          </div>
          <Row justify="end" align="middle" gutter={16} className="mt-4">
            <Col>
              <BtnClear
                label="Close"
                size="sm"
                onClick={() => setShowPreview(false)}
              />
            </Col>
            <Col>
              <BtnAdd
                label="Submit"
                size="sm"
                skipConfirm={true}
                onClick={() => {
                  setShowPreview(false);
                  addPhysicalExam();
                }}
                isClicked={showLoader}
              />
            </Col>
          </Row>
        </Modal>
      )}
    </div>
  );
}

export default PhysicalExaminationYakap;
